import { MoreVertical } from 'lucide-react';
import React from 'react';
 
interface TermsConditionsProps {
  onOpenMenu: () => void;
}
 
export default function TermsConditions({ onOpenMenu }: TermsConditionsProps) {
  return (
    <div className="h-screen md:h-auto bg-[#F5F5F5] flex flex-col">
      <div className="bg-[#FFFFFF] px-4 pt-6 pb-4 flex items-start justify-between border-b border-[#E0E0E0]">
        <h1 className="text-[28px] font-[700] leading-[1.2] text-[#212121] m-0 flex-1 pr-4">
         Terms and Conditions
        </h1>
       
        <button
          onClick={onOpenMenu}
          className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center bg-transparent border-0 cursor-pointer flex-shrink-0"
          aria-label="Open menu"
        >
          <MoreVertical className="w-6 h-6 text-brand-primary" />
        </button>
      </div>
 
      <div className="flex-1 overflow-y-auto bg-[#FFFFFF] px-4 py-6">
        <div className="max-w-[640px] mx-auto">
         
          <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">Terms and Conditions<br></br>Welcome to ISentryx, a dating platform designed for healthcare professionals. By accessing or using the ISentryx application ("App") or our website (collectively, the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service.</p>
          
          <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
            1. Acceptance of Terms
          </h2>
          <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">By registering for, accessing, or using the Service, you confirm that you are at least 18 years old and have the legal capacity to enter into this agreement. These Terms constitute a legally binding agreement between you and ISentryx ("we," "us," or "our").</p>

          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
             2. Eligibility
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
             To use the Service, you must:
            </p>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
- Be at least 18 years of age.<br></br>
- Be a healthcare professional or a student in a healthcare-related field (e.g., doctor, nurse, medical student, pharmacist, etc.).<br></br>
- Provide accurate and truthful information during registration.<br></br>
- Not be prohibited from using the Service under applicable law or by any contractual obligation.
            </p>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
    We reserve the right to verify your professional credentials and suspend or terminate accounts that do not meet eligibility requirements.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              3. Account Registration and Security
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              - <strong>Registration</strong>: You must create an account to access most features of the Service. You agree to provide accurate, current, and complete information during the registration process and to update such information as necessary.<br></br>
- <strong>Account Security</strong>: You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us immediately at <a href="mailto:support@ISentryx.com">support@ISentryx.com</a> if you suspect unauthorized use of your account.<br></br>
- <strong>Account Termination</strong>: We may suspend or terminate your account at our discretion, including for violations of these Terms, fraudulent activity, or inappropriate conduct.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
             4. User Conduct
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
             You agree not to:<br></br>
- Use the Service for any unlawful or unauthorized purpose.<br></br>
- Harass, abuse, or harm other users, including sending unsolicited messages or inappropriate content.<br></br>
- Misrepresent your identity, professional credentials, or affiliations.<br></br>
- Post or transmit any content that is defamatory, obscene, offensive, or violates the rights of others.<br></br>
- Attempt to gain unauthorized access to the Service or its systems.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
             5. Content and Intellectual Property
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
             - <strong>User Content</strong>: You retain ownership of content you post on the Service (e.g., profile information, photos, messages). By posting content, you grant us a non-exclusive, worldwide, royalty-free license to use, store, display, and distribute such content solely for the purpose of operating and improving the Service.<br></br>
- <strong>Our Content</strong>: All content provided by ISentryx, including text, graphics, logos, and software, is our property or the property of our licensors and is protected by intellectual property laws. You may not reproduce, modify, or distribute our content without our prior written consent.<br></br>
- <strong>Content Moderation</strong>: We reserve the right to review, remove, or modify any user content that violates these Terms or is otherwise objectionable.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              6. Privacy
            </h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
              Your privacy is important to us. By using the Service, you consent to our data practices as described in the Privacy Policy.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              7. Termination</h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
             - <strong>By You</strong>: You may terminate your account at any time by following the instructions in the App or contacting us.<br></br>
- <strong>By Us</strong>: We may suspend or terminate your access to the Service at any time, with or without notice, for any reason, including violation of these Terms.<br></br>
- <strong>Effect of Termination</strong>: Upon termination, your right to use the Service will cease, and we may delete your account and associated data, subject to applicable law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              8. Disclaimers</h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
             - <strong>As-Is Basis</strong>: The Service is provided "as is" and "as available" without warranties of any kind, express or implied, including warranties of merchantability, fitness for a particular purpose, or non-infringement.<br></br>
- <strong>No Guarantee of Matches</strong>: We do not guarantee that you will find a match or that other users will meet your expectations.<br></br>
- <strong>Third-Party Interactions</strong>: Your interactions with other users or third parties through the Service are at your own risk. We are not responsible for the conduct of any user or third party.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              9. Limitation of Liability</h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
             To the fullest extent permitted by law, ISentryx and its affiliates, officers, directors, employees, and agents will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or data, arising out of or in connection with your use of the Service. Our total liability to you for any claim arising from these Terms or the Service will not exceed the amount you paid us, if any, for the Service in the six months prior to the claim.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
              10. Indemnification</h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
            You agree to indemnify and hold harmless ISentryx and its affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, losses, or expenses (including reasonable attorneys' fees) arising out of your use of the Service, violation of these Terms, or infringement of any third-party rights.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
             11. Governing Law and Dispute Resolution</h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
          - <strong>Governing Law</strong>: These Terms are governed by the laws of the State of Illinois , USA, without regard to its conflict of law principles.<br></br>
- <strong>Arbitration</strong>: Any disputes arising out of or relating to these Terms or the Service will be resolved through binding arbitration in accordance with the rules <br></br>
- <strong>Class Action Waiver</strong>: You agree that any disputes will be resolved on an individual basis, and you waive any right to participate in a class action or collective proceeding.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
             12. Changes to These Terms</h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
          We may update these Terms from time to time. We will notify you of material changes by posting the updated Terms on the Service or by sending you a notification. Your continued use of the Service after such changes constitutes your acceptance of the updated Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-[16px] font-[700] text-[#212121] mb-3">
             13. Contact Us</h2>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
        If you have any questions or concerns about these Terms or the Service, please contact us at:
            </p>
            <p className="text-[14px] font-[400] text-[#212121] leading-[1.6] mb-3">
<strong>ISentryx-</strong><br></br>
<strong>Email:</strong> <a href="mailto:support@ISentryx.com">support@ISentryx.com</a><br></br>
By using ISentryx, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </section>
         
        </div>
      </div>
    </div>
  );
}